import React from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

const DEFAULT_TIER = {
  tier_name: '',
  price: 0,
  quantity_available: 0,
  is_unlimited: false,
  attendees_per_pass: 1,
  description: '',
  is_comp: false,
  type: 'entry',
  allow_quantity_selection: true,
  min_quantity: '',
  max_quantity: '',
};

export function TierForm({ tier, onChange, showTypeSelector = true, compact = false }) {
  const t = { ...DEFAULT_TIER, ...tier };
  const set = (field, value) => onChange({ ...t, [field]: value });
  const isEntry = t.type === 'entry';
  const isAddon = t.type === 'addon';
  const qtyEnabled = t.allow_quantity_selection !== false;

  const inputCls = `mt-1 bg-[#050505] border-[#1A1A1A] text-[#F3EEE7] focus:border-[#D4AF37] ${compact ? 'text-sm' : ''}`;
  const labelCls = compact ? 'text-[#F3EEE7] text-sm' : 'text-[#EAE7E1]';
  const hintCls = compact ? 'text-[10px] text-[#555] mt-0.5' : 'text-xs text-[#AFAFAF] mt-1';

  return (
    <div className="space-y-3">
      {/* Row 1: Type + Name */}
      <div className={showTypeSelector ? 'grid grid-cols-2 gap-3' : ''}>
        {showTypeSelector && (
          <div>
            <Label className={labelCls}>Type *</Label>
            <select
              value={t.type}
              onChange={(e) => {
                const newType = e.target.value;
                onChange({ ...t, type: newType, requires_access_code: newType === 'addon' ? false : t.requires_access_code });
              }}
              className={`w-full mt-1 px-3 py-2 bg-[#050505] border border-[#1A1A1A] rounded-sm text-[#F3EEE7] focus:border-[#D4AF37] ${compact ? 'text-sm' : ''}`}
              data-testid="tier-form-type"
            >
              <option value="entry">Entry Pass</option>
              <option value="addon">Add-On</option>
            </select>
          </div>
        )}
        <div className={showTypeSelector ? '' : 'w-full'}>
          <Label className={labelCls}>Tier Name *</Label>
          <Input
            value={t.tier_name}
            onChange={(e) => set('tier_name', e.target.value)}
            placeholder={isAddon ? 'e.g., Table Upgrade, Bar Access' : 'e.g., General Admission, VIP'}
            className={inputCls}
            data-testid="tier-form-name"
          />
        </div>
      </div>

      {/* Row 2: Price + Quantity */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className={labelCls}>Price ($)</Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={t.price}
            onChange={(e) => set('price', e.target.value)}
            disabled={t.is_comp}
            className={inputCls}
            data-testid="tier-form-price"
          />
        </div>
        <div>
          <Label className={labelCls}>Qty Available</Label>
          <Input
            type="number"
            min="0"
            value={t.is_unlimited ? '' : t.quantity_available}
            onChange={(e) => set('quantity_available', e.target.value)}
            placeholder={t.is_unlimited ? 'Unlimited' : '0'}
            disabled={!!t.is_unlimited}
            className={`${inputCls} ${t.is_unlimited ? 'opacity-40' : ''}`}
            data-testid="tier-form-qty"
          />
        </div>
      </div>

      {/* Unlimited toggle */}
      <label className="flex items-center gap-2 cursor-pointer" data-testid="tier-form-unlimited-label">
        <input
          type="checkbox"
          checked={!!t.is_unlimited}
          onChange={(e) => onChange({ ...t, is_unlimited: e.target.checked, quantity_available: e.target.checked ? 0 : t.quantity_available })}
          className="rounded"
          data-testid="tier-form-unlimited"
        />
        <span className={labelCls}>Unlimited availability</span>
      </label>

      {/* Entry-only: Attendees per pass */}
      {isEntry && (
        <div>
          <Label className={labelCls}>Attendees Per Pass</Label>
          <Input
            type="number"
            min="1"
            value={t.attendees_per_pass}
            onChange={(e) => set('attendees_per_pass', e.target.value)}
            className={inputCls}
            data-testid="tier-form-attendees"
          />
          <p className={hintCls}>1 = single, 2 = couple, etc.</p>
        </div>
      )}

      {/* Description */}
      <div>
        <Label className={labelCls}>Description (optional)</Label>
        <Textarea
          value={t.description || ''}
          onChange={(e) => set('description', e.target.value)}
          placeholder={isAddon ? 'e.g., Reserved bar table for 6' : 'e.g., Includes VIP lounge access'}
          rows={2}
          className={inputCls}
          data-testid="tier-form-description"
        />
      </div>

      {/* Entry-only: Complimentary */}
      {isEntry && (
        <label className="flex items-center gap-2 cursor-pointer" data-testid="tier-form-comp-label">
          <input
            type="checkbox"
            checked={!!t.is_comp}
            onChange={(e) => onChange({ ...t, is_comp: e.target.checked, price: e.target.checked ? 0 : t.price })}
            className="rounded"
            data-testid="tier-form-comp"
          />
          <span className={`${labelCls}`}>Complimentary (Free)</span>
        </label>
      )}

      {/* Quantity control — progressive disclosure */}
      <div className="border-t border-[#1A1A1A] pt-3">
        <label className="flex items-center gap-2 cursor-pointer mb-2" data-testid="tier-form-qty-toggle-label">
          <input
            type="checkbox"
            checked={qtyEnabled}
            onChange={(e) => set('allow_quantity_selection', e.target.checked)}
            className="rounded"
            data-testid="tier-form-qty-toggle"
          />
          <span className={labelCls}>Guests can choose quantity</span>
          <span className={`text-[10px] ${qtyEnabled ? 'text-[#C6A66B]' : 'text-[#555]'}`}>
            {qtyEnabled ? '(adjustable)' : '(fixed at 1)'}
          </span>
        </label>
        {qtyEnabled && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className={compact ? 'text-xs text-[#F3EEE7]' : 'text-xs text-[#EAE7E1]'}>Min Qty</Label>
              <Input
                type="number"
                min="1"
                value={t.min_quantity || ''}
                onChange={(e) => set('min_quantity', e.target.value)}
                placeholder="1"
                className={inputCls}
                data-testid="tier-form-min-qty"
              />
            </div>
            <div>
              <Label className={compact ? 'text-xs text-[#F3EEE7]' : 'text-xs text-[#EAE7E1]'}>Max Qty</Label>
              <Input
                type="number"
                min="1"
                value={t.max_quantity || ''}
                onChange={(e) => set('max_quantity', e.target.value)}
                placeholder="No limit"
                className={inputCls}
                data-testid="tier-form-max-qty"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Normalize a tier object for API submission.
 * Call this before POSTing/PUTing to the backend.
 */
export function normalizeTier(tier) {
  const isUnlimited = !!tier.is_unlimited;
  return {
    ...tier,
    price: parseFloat(tier.price) || 0,
    quantity_available: isUnlimited ? 0 : (parseInt(tier.quantity_available) || 0),
    is_unlimited: isUnlimited,
    attendees_per_pass: tier.type === 'entry' ? (parseInt(tier.attendees_per_pass) || 1) : 1,
    allow_quantity_selection: tier.allow_quantity_selection !== false,
    min_quantity: tier.min_quantity ? parseInt(tier.min_quantity) : null,
    max_quantity: tier.max_quantity ? parseInt(tier.max_quantity) : null,
    is_comp: tier.type === 'entry' ? !!tier.is_comp : false,
    status_default: tier.status_default || 'active',
    display_order: tier.display_order || 0,
  };
}
